import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search,
    FileText,
    UserCheck,
    FileSignature,
    X,
    CheckCircle,
    XCircle,
    Ban,
    Info,
    Save,
    Eye,
    Download,
} from 'lucide-react';
import './Actionspreembauche.css';
import {
    annulerCandidatureRequest,
    fetchCandidaturesRequest,
    rejeterCandidatureRequest,
    validerCandidatureRequest,
} from '../../../../redux/actions/candidaturesActions';
import { getAllUsersRequest } from '../../../../redux/actions/userActions';
import { fetchOffreSalarialeByCandidatureRequest } from '../../../../redux/actions/offresSalarialesActions';
import {
    exportPreEmbaucheDocumentPdfRequest,
    fetchPreEmbaucheDocumentRequest,
    generatePreEmbaucheDocumentRequest,
    updatePreEmbaucheDocumentRequest,
} from '../../../../redux/actions/preEmbaucheActions';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const CONTRACT_TYPES = ['CDI', 'CDD'];

const PROCEDURE_TEMPLATES = [
    { label: 'Préparation du poste de travail', responsable: 'IT' },
    { label: 'Création des accès informatiques', responsable: 'IT' },
    { label: 'Création de l’adresse email professionnelle', responsable: 'IT' },
    { label: 'Attribution du matériel', responsable: 'IT' },
    { label: 'Préparation des documents RH', responsable: 'RH' },
    { label: 'Présentation de l’équipe', responsable: 'Manager' },
    { label: 'Planification de la formation d’intégration', responsable: 'Formation' },
    { label: 'Validation finale de l’intégration', responsable: 'RH' },
];

const INTEGRATION_DOCUMENT_DEFAULTS = {
    pieceIdentite: { checked: false, type: '', file: null },
    bulletinPaie: { checked: false, file: null },
    autre: { checked: false, file: null },
};

const PIECE_IDENTITE_TYPES = ['CIN', 'Passeport', 'Carte séjour'];

const normalizeStatus = (value) =>
    String(value || '')
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const formatDateInputValue = (value) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
};

const formatDateLabel = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('fr-FR');
};

const toSlug = (value) =>
    String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const getCandidatNom = (candidature) =>
    `${candidature?.candidatPrenom || candidature?.candidatTheque?.prenom || ''} ${
        candidature?.candidatNom || candidature?.candidatTheque?.nom || ''
    }`.trim() || '-';

const getCandidatEmail = (candidature) =>
    candidature?.candidatEmail || candidature?.candidatTheque?.email || '';

const getCandidatPhone = (candidature) =>
    candidature?.candidatPhone || candidature?.candidatTelephone || candidature?.candidatTheque?.phone || '';

const getDisplayedPoste = (candidature) => {
    if (candidature?.autrePoste?.trim()) return candidature.autrePoste.trim();
    return (
        candidature?.posteNom ||
        candidature?.poste ||
        candidature?.demandeRecrutement?.poste?.nom ||
        candidature?.demandeRecrutement?.demandeur?.poste?.nom ||
        '-'
    );
};

const getUserDisplayNameById = (users, userId) => {
    if (!userId) return '-';
    const user = (users || []).find((item) => Number(item.id) === Number(userId));
    if (!user) return `ID ${userId}`;
    if (user.fullName && String(user.fullName).trim()) return user.fullName;
    const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return full || `ID ${userId}`;
};

const getManagerName = (candidature, users) => {
    const nestedManager = candidature?.demandeRecrutement?.demandeur;
    const nestedName = `${nestedManager?.prenom || ''} ${nestedManager?.nom || ''}`.trim();
    return nestedName || candidature?.demandeurNom || getUserDisplayNameById(users, candidature?.demandeurId);
};

const getStatutCandidature = (candidature) =>
    candidature?.statutDeCandidature || candidature?.statut || '-';

const getOffreSalarialeStatut = (candidature, offreByCandidature) =>
    offreByCandidature?.[candidature.id]?.statutOffre ||
    candidature?.offreSalariale?.statutOffre ||
    candidature?.statutOffre ||
    '';

const getOffreSalaire = (candidature, offreByCandidature) => {
    const offre = offreByCandidature?.[candidature?.id] || candidature?.offreSalariale || {};
    return offre?.salaireBase || offre?.salaire || offre?.montant || '';
};

const getOffreSalaireLabel = (candidature, offreByCandidature) => {
    const salaire = getOffreSalaire(candidature, offreByCandidature);
    if (salaire === '' || salaire === null || typeof salaire === 'undefined') return '-';
    return `${salaire}`;
};

const getDatePrisePoste = (candidature, offreByCandidature) => {
    const offre = offreByCandidature?.[candidature?.id] || candidature?.offreSalariale || {};
    return formatDateInputValue(
        offre?.dateDebut ||
            offre?.datePrisePoste ||
            candidature?.datePrisePoste ||
            candidature?.dateDebutPrevue ||
            ''
    );
};

const getDepartment = (candidature) =>
    candidature?.departement ||
    candidature?.departementNom ||
    candidature?.demandeRecrutement?.departement?.nom ||
    candidature?.demandeRecrutement?.entite?.nom ||
    '';

const isPreEmbaucheEligible = (candidature, offreByCandidature) =>
    normalizeStatus(candidature?.decisionFinale) === 'VALIDE' &&
    ['ACCEPTE', 'ACCEPTEE'].includes(normalizeStatus(getOffreSalarialeStatut(candidature, offreByCandidature)));

const buildCandidateContext = (candidature, users, offreByCandidature) => ({
    candidatureId: candidature?.id,
    candidatNomComplet: getCandidatNom(candidature),
    poste: getDisplayedPoste(candidature),
    manager: getManagerName(candidature, users),
    email: getCandidatEmail(candidature),
    telephone: getCandidatPhone(candidature),
    departement: getDepartment(candidature),
    dateGeneration: formatDateInputValue(new Date()),
    datePrisePoste: getDatePrisePoste(candidature, offreByCandidature),
    salaire: getOffreSalaireLabel(candidature, offreByCandidature),
    salaireValue: getOffreSalaire(candidature, offreByCandidature),
    horairesTravail:
        candidature?.horairesTravail ||
        candidature?.regimeTravail ||
        candidature?.demandeRecrutement?.regimeTravail ||
        'Temps plein',
    lieuTravail:
        candidature?.lieuTravail ||
        candidature?.etablissementNom ||
        candidature?.autreEtablissement ||
        candidature?.demandeRecrutement?.etablissement?.nom ||
        'À définir',
});

const computeProcedureStatus = (steps) => {
    if (!(steps || []).length) return 'NON_LANCEE';
    const normalized = steps.map((step) => normalizeStatus(step.status));
    if (normalized.every((status) => status === 'TERMINE')) return 'TERMINEE';
    if (normalized.some((status) => status === 'EN_COURS' || status === 'TERMINE' || status === 'BLOQUE')) {
        return 'EN_COURS';
    }
    return 'NON_LANCEE';
};

const buildDefaultLetter = (context, existing = null) => {
    const base = existing || {};
    const content =
        base.content ||
        `Nous avons le plaisir de vous confirmer votre recrutement au poste de ${context.poste} au sein de notre société.\n\n` +
            `Votre prise de poste est prévue le ${formatDateLabel(context.datePrisePoste)} sous la responsabilité de ${context.manager}.\n\n` +
            `Les éléments connus de votre offre salariale s’élèvent à ${context.salaire}.\n\n` +
            `Nous vous remercions pour votre confiance et restons à votre disposition pour finaliser les prochaines étapes administratives.\n\n` +
            `Cordialement,\nDirection des Ressources Humaines`;

    return {
        candidatureId: context.candidatureId,
        candidatNomComplet: context.candidatNomComplet,
        poste: context.poste,
        manager: context.manager,
        email: context.email,
        telephone: context.telephone,
        departement: context.departement || '',
        dateGeneration: base.dateGeneration || context.dateGeneration,
        datePrisePoste: base.datePrisePoste || context.datePrisePoste,
        salaire: base.salaire || context.salaire,
        content,
        status: base.status || 'BROUILLON',
        statusLabel: base.statusLabel || 'Brouillon',
        source: base.source || 'ui',
        isMocked: !!base.isMocked,
        lastSavedAt: base.lastSavedAt || '',
        exportedAt: base.exportedAt || '',
    };
};

const buildDefaultContract = (context, existing = null) => {
    const base = existing || {};
    const typeContrat = CONTRACT_TYPES.includes(base.typeContrat) ? base.typeContrat : 'CDI';

    return {
        candidatureId: context.candidatureId,
        candidatNomComplet: context.candidatNomComplet,
        poste: context.poste,
        manager: context.manager,
        email: context.email,
        telephone: context.telephone,
        offreSalariale: base.offreSalariale || context.salaire,
        typeContrat,
        dateDebut: base.dateDebut || context.datePrisePoste,
        dateFin: base.dateFin || '',
        periodeEssai: base.periodeEssai || '3 mois',
        salaire: base.salaire || context.salaire,
        lieuTravail: base.lieuTravail || context.lieuTravail,
        regimeTravail: base.regimeTravail || base.horairesTravail || context.horairesTravail,
        commentaireRh: base.commentaireRh || '',
        contenu:
            base.contenu ||
            `Contrat de travail ${typeContrat}\n\n` +
                `Candidat : ${context.candidatNomComplet}\n` +
                `Poste : ${context.poste}\n` +
                `Date de début : ${formatDateLabel(base.dateDebut || context.datePrisePoste)}\n` +
                `Salaire : ${base.salaire || context.salaire}\n` +
                `Lieu de travail : ${base.lieuTravail || context.lieuTravail}\n` +
                `Régime de travail : ${base.regimeTravail || base.horairesTravail || context.horairesTravail}\n`,
        status: base.status || 'NON_GENERE',
        statusLabel: base.statusLabel || 'Non généré',
        source: base.source || 'ui',
        isMocked: !!base.isMocked,
        lastSavedAt: base.lastSavedAt || '',
        exportedAt: base.exportedAt || '',
    };
};

const buildDefaultProcedure = (context, existing = null) => {
    const base = existing || {};
    const steps =
        (base.steps || []).length > 0
            ? base.steps.map((step, index) => ({
                  id: step.id || `${context.candidatureId}-${index + 1}`,
                  label: step.label || '',
                  responsable: step.responsable || 'RH',
                  status: step.status || 'A_FAIRE',
                  statusLabel: step.statusLabel || 'À faire',
                  datePrevue: formatDateInputValue(step.datePrevue),
                  dateRealisation: formatDateInputValue(step.dateRealisation),
                  commentaire: step.commentaire || '',
              }))
            : PROCEDURE_TEMPLATES.map((template, index) => ({
                  id: `${context.candidatureId}-${index + 1}`,
                  label: template.label,
                  responsable: template.responsable,
                  status: 'A_FAIRE',
                  statusLabel: 'À faire',
                  datePrevue: '',
                  dateRealisation: '',
                  commentaire: '',
              })).slice(0, 8);

    const limitedSteps = steps.slice(0, 8);
    const status = base.status || computeProcedureStatus(limitedSteps);

    return {
        candidatureId: context.candidatureId,
        candidatNomComplet: context.candidatNomComplet,
        poste: context.poste,
        manager: context.manager,
        email: context.email,
        telephone: context.telephone,
        status,
        statusLabel: base.statusLabel || getDocumentStatusMeta('procedureIntegration', status).label,
        steps: limitedSteps,
        documentsIntegration: {
            pieceIdentite: {
                ...INTEGRATION_DOCUMENT_DEFAULTS.pieceIdentite,
                ...(base.documentsIntegration?.pieceIdentite || {}),
                file: null,
            },
            bulletinPaie: {
                ...INTEGRATION_DOCUMENT_DEFAULTS.bulletinPaie,
                ...(base.documentsIntegration?.bulletinPaie || {}),
                file: null,
            },
            autre: {
                ...INTEGRATION_DOCUMENT_DEFAULTS.autre,
                ...(base.documentsIntegration?.autre || {}),
                file: null,
            },
        },
        source: base.source || 'ui',
        isMocked: !!base.isMocked,
        lastSavedAt: base.lastSavedAt || '',
        exportedAt: base.exportedAt || '',
    };
};

const getStatusClass = (status) => {
    switch (normalizeStatus(status)) {
        case 'TRAITEMENT ADMINISTRATIF':
        case 'TRAITEMENT_ADMINISTRATIF':
            return 'apePill--processing';
        case 'CONFIRMEE':
        case 'VALIDEE':
            return 'apePill--confirmed';
        case 'REJETEE':
        case 'REFUSEE':
            return 'apePill--rejected';
        case 'ANNULEE':
            return 'apePill--cancelled';
        default:
            return '';
    }
};

const getDocumentStatusMeta = (documentType, status) => {
    const normalized = normalizeStatus(status);

    if (documentType === 'procedureIntegration') {
        switch (normalized) {
            case 'TERMINEE':
                return { label: 'Terminée', className: 'apeDocStatus--success' };
            case 'EN_COURS':
                return { label: 'En cours', className: 'apeDocStatus--info' };
            default:
                return { label: 'Non lancée', className: 'apeDocStatus--muted' };
        }
    }

    switch (normalized) {
        case 'GENERE':
        case 'GENEREE':
            return { label: 'Généré', className: 'apeDocStatus--success' };
        case 'MODIFIE':
        case 'MODIFIEE':
            return { label: 'Modifié', className: 'apeDocStatus--warning' };
        case 'EXPORTE':
        case 'EXPORTEE':
            return { label: 'Exporté', className: 'apeDocStatus--info' };
        case 'BROUILLON':
            return { label: 'Brouillon', className: 'apeDocStatus--warning' };
        default:
            return { label: 'Non généré', className: 'apeDocStatus--muted' };
    }
};

const applyLetterStatus = (draft, status) => {
    const meta = getDocumentStatusMeta('lettreConfirmation', status);
    return {
        ...draft,
        status,
        statusLabel: meta.label,
        lastSavedAt: status === 'EXPORTEE' ? draft.lastSavedAt : new Date().toISOString(),
        exportedAt: status === 'EXPORTEE' ? new Date().toISOString() : draft.exportedAt,
    };
};

const applyContractStatus = (draft, status) => {
    const meta = getDocumentStatusMeta('contratTravail', status);
    return {
        ...draft,
        status,
        statusLabel: meta.label,
        lastSavedAt: status === 'EXPORTE' ? draft.lastSavedAt : new Date().toISOString(),
        exportedAt: status === 'EXPORTE' ? new Date().toISOString() : draft.exportedAt,
    };
};

const applyProcedureStatus = (draft) => {
    const status = computeProcedureStatus(draft.steps);
    const meta = getDocumentStatusMeta('procedureIntegration', status);
    return {
        ...draft,
        status,
        statusLabel: meta.label,
        lastSavedAt: new Date().toISOString(),
    };
};

const Modal = ({ open, title, children, footer, onClose, size = 'md' }) => {
    if (!open) return null;
    const sizeClass = size === 'lg' ? 'apeModal__panel--lg' : '';
    const compactClass = size === 'sm' ? 'apeModal__panel--sm' : '';
    return (
        <div className="apeModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`apeModal__panel ${sizeClass} ${compactClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="apeModal__header">
                    <div className="apeModal__title">{title}</div>
                    <button className="apeIconBtn" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="apeModal__body">{children}</div>
                {footer && <div className="apeModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

const DocumentStatusPill = ({ documentType, status }) => {
    const meta = getDocumentStatusMeta(documentType, status);
    return <span className={`apeDocStatus ${meta.className}`}>{meta.label}</span>;
};

export default function ActionsPreEmbauche() {
    const dispatch = useDispatch();
    const candidaturesState = useSelector((state) => state.candidatures || {});
    const offresSalarialesState = useSelector((state) => state.offresSalariales || {});
    const preEmbaucheState = useSelector((state) => state.preEmbauche || {});
    const users = useSelector((state) => state.user?.users || EMPTY_ARRAY);
    const candidatures = candidaturesState.candidatures || EMPTY_ARRAY;
    const loading = !!candidaturesState.loading;
    const offreByCandidature = offresSalarialesState.offreByCandidature || EMPTY_OBJECT;

    const lettreByCandidature = preEmbaucheState.lettreConfirmationByCandidature || EMPTY_OBJECT;
    const contratByCandidature = preEmbaucheState.contratTravailByCandidature || EMPTY_OBJECT;
    const procedureByCandidature = preEmbaucheState.procedureIntegrationByCandidature || EMPTY_OBJECT;
    const loadingByKey = preEmbaucheState.loadingByKey || EMPTY_OBJECT;

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showLetterModal, setShowLetterModal] = useState(false);
    const [showProcedureModal, setShowProcedureModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [showRejetModal, setShowRejetModal] = useState(false);
    const [showAnnulerModal, setShowAnnulerModal] = useState(false);
    const [showMotifModal, setShowMotifModal] = useState(false);

    const [selectedCandidature, setSelectedCandidature] = useState(null);
    const [motif, setMotif] = useState('');
    const [letterDraft, setLetterDraft] = useState(null);
    const [contractDraft, setContractDraft] = useState(null);
    const [procedureDraft, setProcedureDraft] = useState(null);

    const [filters, setFilters] = useState({
        nomCandidat: '',
        email: '',
        phone: '',
        nomManager: '',
        statut: '',
    });

    useEffect(() => {
        dispatch(fetchCandidaturesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    useEffect(() => {
        (candidatures || [])
            .filter((candidature) => normalizeStatus(candidature?.decisionFinale) === 'VALIDE')
            .forEach((candidature) => {
                if (!offreByCandidature[candidature.id]) {
                    dispatch(fetchOffreSalarialeByCandidatureRequest(candidature.id));
                }
            });
    }, [dispatch, candidatures, offreByCandidature]);

    const candidaturesPreEmbauche = useMemo(
        () => candidatures.filter((candidature) => isPreEmbaucheEligible(candidature, offreByCandidature)),
        [candidatures, offreByCandidature]
    );

    const filteredCandidatures = useMemo(() => {
        return candidaturesPreEmbauche.filter((candidature) => {
            const candidateName = getCandidatNom(candidature).toLowerCase();
            const email = getCandidatEmail(candidature).toLowerCase();
            const phone = getCandidatPhone(candidature).toLowerCase();
            const manager = getManagerName(candidature, users).toLowerCase();
            const statut = getStatutCandidature(candidature);

            return (
                (!filters.nomCandidat || candidateName.includes(filters.nomCandidat.toLowerCase())) &&
                (!filters.email || email.includes(filters.email.toLowerCase())) &&
                (!filters.phone || phone.includes(filters.phone.toLowerCase())) &&
                (!filters.nomManager || manager.includes(filters.nomManager.toLowerCase())) &&
                (!filters.statut || statut === filters.statut)
            );
        });
    }, [candidaturesPreEmbauche, filters, users]);

    const statusOptions = useMemo(
        () => [...new Set(candidaturesPreEmbauche.map((candidature) => getStatutCandidature(candidature)).filter(Boolean))],
        [candidaturesPreEmbauche]
    );

    const selectedContext = useMemo(() => {
        if (!selectedCandidature) return null;
        return buildCandidateContext(selectedCandidature, users, offreByCandidature);
    }, [selectedCandidature, users, offreByCandidature]);

    const currentLetter = selectedCandidature ? lettreByCandidature[selectedCandidature.id] : null;
    const currentContract = selectedCandidature ? contratByCandidature[selectedCandidature.id] : null;
    const currentProcedure = selectedCandidature ? procedureByCandidature[selectedCandidature.id] : null;

    useEffect(() => {
        if (showLetterModal && selectedContext) {
            setLetterDraft(buildDefaultLetter(selectedContext, currentLetter));
        }
    }, [showLetterModal, selectedContext, currentLetter]);

    useEffect(() => {
        if (showContractModal && selectedContext) {
            setContractDraft(buildDefaultContract(selectedContext, currentContract));
        }
    }, [showContractModal, selectedContext, currentContract]);

    useEffect(() => {
        if (showProcedureModal && selectedContext) {
            setProcedureDraft(buildDefaultProcedure(selectedContext, currentProcedure));
        }
    }, [showProcedureModal, selectedContext, currentProcedure]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters({
            nomCandidat: '',
            email: '',
            phone: '',
            nomManager: '',
            statut: '',
        });
    };

    const isAnyFilterFilled = Object.values(filters).some((val) => val !== '');

    const isAnyDocumentLoading = (documentType, candidatureId) =>
        Object.entries(loadingByKey).some(
            ([key, value]) => value && key.startsWith(`${documentType}:${candidatureId}:`)
        );

    const openDetails = (candidature) => {
        setSelectedCandidature(candidature);
        setShowDetailsModal(true);
    };

    const openMotifDetails = (candidature) => {
        setSelectedCandidature(candidature);
        setShowMotifModal(true);
    };

    const openLetter = (candidature) => {
        setSelectedCandidature(candidature);
        setShowLetterModal(true);
        dispatch(fetchPreEmbaucheDocumentRequest('lettreConfirmation', candidature.id));
    };

    const openProcedure = (candidature) => {
        setSelectedCandidature(candidature);
        setShowProcedureModal(true);
        dispatch(fetchPreEmbaucheDocumentRequest('procedureIntegration', candidature.id));
    };

    const openContract = (candidature) => {
        setSelectedCandidature(candidature);
        setShowContractModal(true);
        dispatch(fetchPreEmbaucheDocumentRequest('contratTravail', candidature.id));
    };

    const openRejet = (candidature) => {
        setSelectedCandidature(candidature);
        setMotif('');
        setShowRejetModal(true);
    };

    const openAnnuler = (candidature) => {
        setSelectedCandidature(candidature);
        setMotif('');
        setShowAnnulerModal(true);
    };

    const confirmerCandidature = (candidature = selectedCandidature) => {
        if (!candidature?.id) return;
        dispatch(validerCandidatureRequest(candidature.id));
        setSelectedCandidature(null);
    };

    const rejeterCandidature = () => {
        if (!motif.trim()) {
            alert('Veuillez préciser le motif de rejet');
            return;
        }
        if (!selectedCandidature?.id) return;
        dispatch(rejeterCandidatureRequest(selectedCandidature.id, motif));
        setShowRejetModal(false);
        setSelectedCandidature(null);
        setMotif('');
    };

    const annulerCandidature = () => {
        if (!motif.trim()) {
            alert("Veuillez préciser le motif d'annulation");
            return;
        }
        if (!selectedCandidature?.id) return;
        dispatch(annulerCandidatureRequest(selectedCandidature.id, motif));
        setShowAnnulerModal(false);
        setSelectedCandidature(null);
        setMotif('');
    };

    const isActionDisabled = (candidature) => {
        return ['CONFIRMEE', 'REJETEE', 'REFUSEE', 'ANNULEE'].includes(normalizeStatus(getStatutCandidature(candidature)));
    };

    const updateLetterField = (field, value) => {
        setLetterDraft((prev) => {
            const next = { ...(prev || {}), [field]: value };
            if (!prev) return next;
            if (normalizeStatus(prev.status) === 'GENERE' || normalizeStatus(prev.status) === 'GENEREE') {
                return applyLetterStatus(next, 'MODIFIEE');
            }
            return applyLetterStatus(next, normalizeStatus(prev.status) === 'EXPORTEE' ? 'MODIFIEE' : prev.status || 'BROUILLON');
        });
    };

    const updateContractField = (field, value) => {
        setContractDraft((prev) => {
            const next = { ...(prev || {}), [field]: value };
            if (!prev) return next;
            if (normalizeStatus(prev.status) === 'GENERE' || normalizeStatus(prev.status) === 'GENEREE') {
                return applyContractStatus(next, 'MODIFIE');
            }
            return applyContractStatus(next, normalizeStatus(prev.status) === 'EXPORTE' ? 'MODIFIE' : prev.status || 'BROUILLON');
        });
    };

    const updateIntegrationDocument = (documentKey, field, value) => {
        setProcedureDraft((prev) => {
            if (!prev) return prev;
            const documentsIntegration = prev.documentsIntegration || INTEGRATION_DOCUMENT_DEFAULTS;
            const nextDocument = {
                ...(documentsIntegration[documentKey] || {}),
                [field]: value,
            };

            if (field === 'checked' && !value) {
                nextDocument.file = null;
                if (documentKey === 'pieceIdentite') nextDocument.type = '';
            }

            return {
                ...prev,
                documentsIntegration: {
                    ...documentsIntegration,
                    [documentKey]: nextDocument,
                },
                lastSavedAt: new Date().toISOString(),
            };
        });
    };

    const getIntegrationDocumentFileName = (documentKey) => {
        return procedureDraft?.documentsIntegration?.[documentKey]?.file?.name || 'Aucun fichier n’a été sélectionné';
    };

    const handleGenerateLetter = () => {
        if (!selectedCandidature || !letterDraft) return;
        const next = applyLetterStatus(
            {
                ...letterDraft,
                dateGeneration: formatDateInputValue(new Date()),
            },
            'GENEREE'
        );
        setLetterDraft(next);
        dispatch(generatePreEmbaucheDocumentRequest('lettreConfirmation', selectedCandidature.id, next));
    };

    const handleSaveLetter = () => {
        if (!selectedCandidature || !letterDraft) return;
        const next = applyLetterStatus(letterDraft, normalizeStatus(letterDraft.status) === 'NON_GENERE' ? 'BROUILLON' : letterDraft.status || 'BROUILLON');
        setLetterDraft(next);
        dispatch(updatePreEmbaucheDocumentRequest('lettreConfirmation', selectedCandidature.id, next));
    };

    const handleExportLetter = () => {
        if (!selectedCandidature || !letterDraft) return;
        const next = applyLetterStatus(letterDraft, 'EXPORTEE');
        setLetterDraft(next);
        dispatch(exportPreEmbaucheDocumentPdfRequest('lettreConfirmation', selectedCandidature.id, next));
    };

    const handleGenerateContract = () => {
        if (!selectedCandidature || !contractDraft) return;
        const next = applyContractStatus(contractDraft, 'GENERE');
        setContractDraft(next);
        dispatch(generatePreEmbaucheDocumentRequest('contratTravail', selectedCandidature.id, next));
    };

    const handleSaveContract = () => {
        if (!selectedCandidature || !contractDraft) return;
        const next = applyContractStatus(
            contractDraft,
            normalizeStatus(contractDraft.status) === 'NON_GENERE' ? 'BROUILLON' : contractDraft.status || 'BROUILLON'
        );
        setContractDraft(next);
        dispatch(updatePreEmbaucheDocumentRequest('contratTravail', selectedCandidature.id, next));
    };

    const handleExportContract = () => {
        if (!selectedCandidature || !contractDraft) return;
        const next = applyContractStatus(contractDraft, 'EXPORTE');
        setContractDraft(next);
        dispatch(exportPreEmbaucheDocumentPdfRequest('contratTravail', selectedCandidature.id, next));
    };

    const handleSaveProcedure = () => {
        if (!selectedCandidature || !procedureDraft) return;
        const next = applyProcedureStatus(procedureDraft);
        setProcedureDraft(next);
        dispatch(updatePreEmbaucheDocumentRequest('procedureIntegration', selectedCandidature.id, next));
    };

    React.useEffect(() => {
        const isAnyModalOpen =
            showDetailsModal ||
            showLetterModal ||
            showProcedureModal ||
            showContractModal ||
            showRejetModal ||
            showAnnulerModal ||
            showMotifModal;
        document.body.style.overflow = isAnyModalOpen ? 'hidden' : 'unset';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [
        showDetailsModal,
        showLetterModal,
        showProcedureModal,
        showContractModal,
        showRejetModal,
        showAnnulerModal,
        showMotifModal,
    ]);

    return (
        <div className="apePage">
            <div className="apeHeader">
                <div>
                    <h1>Actions Pré-embauche</h1>
                    <p>Gérez les candidatures en phase de traitement administratif</p>
                </div>
            </div>

            <div className="apeWrap">
                <div className="apeCard">
                    <div className="apeBody">
                        <div className="apeFilterSection">
                            <div className="apeFilterTitle">
                                <Search size={16} />
                                Filtre de recherche
                            </div>

                            <div className="apeFilterGrid">
                                <div className="apeField">
                                    <label>Candidat</label>
                                    <input
                                        type="text"
                                        value={filters.nomCandidat}
                                        onChange={(e) => handleFilterChange('nomCandidat', e.target.value)}
                                        placeholder="Rechercher par nom..."
                                    />
                                </div>

                                <div className="apeField">
                                    <label>Email</label>
                                    <input
                                        type="text"
                                        value={filters.email}
                                        onChange={(e) => handleFilterChange('email', e.target.value)}
                                        placeholder="Rechercher par email..."
                                    />
                                </div>

                                <div className="apeField">
                                    <label>Numéro de téléphone</label>
                                    <input
                                        type="text"
                                        value={filters.phone}
                                        onChange={(e) => handleFilterChange('phone', e.target.value)}
                                        placeholder="Rechercher par téléphone..."
                                    />
                                </div>

                                <div className="apeField">
                                    <label>Manager</label>
                                    <input
                                        type="text"
                                        value={filters.nomManager}
                                        onChange={(e) => handleFilterChange('nomManager', e.target.value)}
                                        placeholder="Rechercher par manager..."
                                    />
                                </div>

                                <div className="apeField">
                                    <label>Statut de candidature</label>
                                    <select
                                        value={filters.statut}
                                        onChange={(e) => handleFilterChange('statut', e.target.value)}
                                    >
                                        <option value="">Tous</option>
                                        {statusOptions.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="apeFilterActions">
                                <button
                                    className={`apeBtn ${isAnyFilterFilled ? 'apeBtn--warning' : 'apeBtn--ghost'}`}
                                    type="button"
                                    onClick={resetFilters}
                                    disabled={!isAnyFilterFilled}
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="apeWrap">
                <div className="apeCard">
                    <div className="apeBody">
                        {/*<div className="apeTopRow">*/}
                        {/*    <h2 className="apeTitle">Actions pré-embauche ({filteredCandidatures.length})</h2>*/}
                        {/*</div>*/}

                        <div className="apeTableWrap">
                            <table className="apeTable">
                                <thead>
                                <tr>
                                    <th>Candidat</th>
                                    <th>Poste</th>
                                    <th>Manager</th>
                                    <th>Statut de candidature</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="apeEmptyCell">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : filteredCandidatures.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="apeEmptyCell">
                                            Aucune candidature trouvée
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCandidatures.map((candidature) => {
                                        const rowBusy =
                                            isAnyDocumentLoading('lettreConfirmation', candidature.id) ||
                                            isAnyDocumentLoading('contratTravail', candidature.id) ||
                                            isAnyDocumentLoading('procedureIntegration', candidature.id);

                                        return (
                                            <tr key={candidature.id}>
                                                <td>
                                                    <div className="apeInline">
                                                        <span className="apeStrong">{getCandidatNom(candidature)}</span>
                                                        <button
                                                            className="apeIconBtn apeIconBtn--info"
                                                            type="button"
                                                            title="Détails candidat"
                                                            onClick={() => openDetails(candidature)}
                                                        >
                                                            <Info size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="apeMuted">{getDisplayedPoste(candidature)}</td>
                                                <td className="apeMuted">{getManagerName(candidature, users)}</td>
                                                <td>
                                                    <div className="apeInline">
                                                        <button
                                                            className={`apePill ${getStatusClass(getStatutCandidature(candidature))}`}
                                                            type="button"
                                                            disabled
                                                        >
                                                            {getStatutCandidature(candidature)}
                                                        </button>
                                                        {['REJETEE', 'REFUSEE', 'ANNULEE'].includes(
                                                            normalizeStatus(getStatutCandidature(candidature))
                                                        ) && (
                                                            <button
                                                                className="apeIconBtn apeIconBtn--danger"
                                                                type="button"
                                                                title="Voir le motif"
                                                                onClick={() => openMotifDetails(candidature)}
                                                            >
                                                                <Info size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="is-right">
                                                    <div className="apeRowActions">
                                                        <button
                                                        className="apeIconBtn"
                                                            type="button"
                                                        title="Lettre de confirmation de recrutement"
                                                            onClick={() => openLetter(candidature)}
                                                        disabled={rowBusy}
                                                        >
                                                            <FileText size={16} />
                                                        </button>
                                                        <button
                                                            className="apeIconBtn"
                                                            type="button"
                                                            title="Procédure d'intégration"
                                                            onClick={() => openProcedure(candidature)}
                                                            disabled={rowBusy}
                                                        >
                                                            <UserCheck size={16} />
                                                        </button>
                                                        <button
                                                        className="apeIconBtn"
                                                            type="button"
                                                        title="Contrat de travail"
                                                            onClick={() => openContract(candidature)}
                                                        disabled={rowBusy}
                                                        >
                                                            <FileSignature size={16} />
                                                        </button>
                                                        <button
                                                            className={`apeIconBtn ${
                                                                isActionDisabled(candidature) ? 'apeIconBtn--disabled' : 'apeIconBtn--danger'
                                                            }`}
                                                            type="button"
                                                            title={isActionDisabled(candidature) ? 'Annuler pas disponible' : 'Annuler'}
                                                            onClick={() => openAnnuler(candidature)}
                                                            disabled={isActionDisabled(candidature)}
                                                        >
                                                            <XCircle size={16} />
                                                        </button>
                                                        <button
                                                            className={`apeIconBtn ${
                                                                isActionDisabled(candidature) ? 'apeIconBtn--disabled' : 'apeIconBtn--success'
                                                            }`}
                                                            type="button"
                                                            title={isActionDisabled(candidature) ? 'Confirmer pas disponible' : 'Confirmer'}
                                                            onClick={() => confirmerCandidature(candidature)}
                                                            disabled={isActionDisabled(candidature)}
                                                        >
                                                            <CheckCircle size={16} />
                                                        </button>
                                                        <button
                                                            className={`apeIconBtn ${
                                                                isActionDisabled(candidature) ? 'apeIconBtn--disabled' : 'apeIconBtn--danger'
                                                            }`}
                                                            type="button"
                                                            title={isActionDisabled(candidature) ? 'Rejeter pas disponible' : 'Rejeter'}
                                                            onClick={() => openRejet(candidature)}
                                                            disabled={isActionDisabled(candidature)}
                                                        >
                                                            <Ban size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                open={showDetailsModal}
                title={`Détails - ${selectedCandidature ? getCandidatNom(selectedCandidature) : ''}`}
                onClose={() => setShowDetailsModal(false)}
                footer={
                    <button className="apeBtn apeBtn--primary" type="button" onClick={() => setShowDetailsModal(false)}>
                        Fermer
                    </button>
                }
            >
                {selectedCandidature && (
                    <div className="apeViewContent">
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Nom complet</div>
                            <div className="apeViewValue">{getCandidatNom(selectedCandidature)}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Email</div>
                            <div className="apeViewValue">{getCandidatEmail(selectedCandidature) || '-'}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Téléphone</div>
                            <div className="apeViewValue">{getCandidatPhone(selectedCandidature) || '-'}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Poste</div>
                            <div className="apeViewValue">{getDisplayedPoste(selectedCandidature)}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Manager</div>
                            <div className="apeViewValue">{getManagerName(selectedCandidature, users)}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Département</div>
                            <div className="apeViewValue">{getDepartment(selectedCandidature) || '-'}</div>
                        </div>
                        <div className="apeViewRow">
                            <div className="apeViewLabel">Statut</div>
                            <div className="apeViewValue">
                                <button
                                    className={`apePill ${getStatusClass(getStatutCandidature(selectedCandidature))}`}
                                    type="button"
                                    disabled
                                >
                                    {getStatutCandidature(selectedCandidature)}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showMotifModal}
                title="Motif"
                onClose={() => setShowMotifModal(false)}
                footer={
                    <button className="apeBtn apeBtn--primary" type="button" onClick={() => setShowMotifModal(false)}>
                        Fermer
                    </button>
                }
            >
                {selectedCandidature && (
                    <div className="apeViewContent">
                        <div className="apeViewRow">
                            <div className="apeViewLabel">
                                {normalizeStatus(getStatutCandidature(selectedCandidature)) === 'REJETEE' ||
                                normalizeStatus(getStatutCandidature(selectedCandidature)) === 'REFUSEE'
                                    ? 'Motif de rejet'
                                    : "Motif d'annulation"}
                            </div>
                            <div className="apeViewValue">
                                {selectedCandidature.motifRejet || selectedCandidature.motifAnnulation || '-'}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showLetterModal}
                title="Lettre de confirmation de recrutement"
                onClose={() => setShowLetterModal(false)}
                size="lg"
                footer={
                    <>
                        <button className="apeBtn apeBtn--ghost" type="button" onClick={() => setShowLetterModal(false)}>
                            Fermer
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleGenerateLetter}
                        >
                            <Eye size={16} />
                            Générer / Prévisualiser
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleSaveLetter}
                        >
                            <Save size={16} />
                            Enregistrer les modifications
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleExportLetter}
                        >
                            <Download size={16} />
                            Exporter en PDF
                        </button>
                    </>
                }
            >
                {letterDraft && (
                    <div className="apeDocumentLayout">
                        <div className="apeDocumentHeader">
                            <DocumentStatusPill documentType="lettreConfirmation" status={letterDraft.status} />
                        </div>

                        <div className="apeInfoGrid">
                            <div className="apeInfoItem"><span>Nom complet</span><strong>{letterDraft.candidatNomComplet}</strong></div>
                            <div className="apeInfoItem"><span>Poste</span><strong>{letterDraft.poste}</strong></div>
                            <div className="apeInfoItem"><span>Manager</span><strong>{letterDraft.manager}</strong></div>
                            <div className="apeInfoItem"><span>Email</span><strong>{letterDraft.email || '-'}</strong></div>
                            <div className="apeInfoItem"><span>Téléphone</span><strong>{letterDraft.telephone || '-'}</strong></div>
                            <div className="apeInfoItem"><span>Département</span><strong>{letterDraft.departement || '-'}</strong></div>
                            <div className="apeInfoItem"><span>Date de génération</span><strong>{formatDateLabel(letterDraft.dateGeneration)}</strong></div>
                            <div className="apeInfoItem"><span>Prise de poste</span><strong>{formatDateLabel(letterDraft.datePrisePoste)}</strong></div>
                            <div className="apeInfoItem"><span>Offre salariale</span><strong>{letterDraft.salaire || '-'}</strong></div>
                        </div>

                        <div className="apeField">
                            <label>Contenu de la lettre</label>
                            <textarea
                                className="apeEditor"
                                rows={16}
                                value={letterDraft.content || ''}
                                onChange={(e) => updateLetterField('content', e.target.value)}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showContractModal}
                title="Contrat de travail"
                onClose={() => setShowContractModal(false)}
                size="lg"
                footer={
                    <>
                        <button className="apeBtn apeBtn--ghost" type="button" onClick={() => setShowContractModal(false)}>
                            Fermer
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleGenerateContract}
                            disabled={!contractDraft || isAnyDocumentLoading('contratTravail', selectedCandidature?.id)}
                        >
                            <FileSignature size={16} />
                            Générer contrat
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleSaveContract}
                            disabled={!contractDraft || isAnyDocumentLoading('contratTravail', selectedCandidature?.id)}
                        >
                            <Save size={16} />
                            Enregistrer
                        </button>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleExportContract}
                            disabled={!contractDraft || isAnyDocumentLoading('contratTravail', selectedCandidature?.id)}
                        >
                            <Download size={16} />
                            Exporter PDF
                        </button>
                    </>
                }
            >
                {contractDraft && (
                    <div className="apeDocumentLayout">
                        <div className="apeDocumentHeader">
                            <DocumentStatusPill documentType="contratTravail" status={contractDraft.status} />
                        </div>

                        <div className="apeInfoGrid">
                            <div className="apeInfoItem"><span>Candidat</span><strong>{contractDraft.candidatNomComplet}</strong></div>
                            <div className="apeInfoItem"><span>Poste</span><strong>{contractDraft.poste}</strong></div>
                            <div className="apeInfoItem"><span>Manager</span><strong>{contractDraft.manager}</strong></div>
                            <div className="apeInfoItem"><span>Email</span><strong>{contractDraft.email || '-'}</strong></div>
                            <div className="apeInfoItem"><span>Téléphone</span><strong>{contractDraft.telephone || '-'}</strong></div>
                            <div className="apeInfoItem"><span>Offre salariale acceptée</span><strong>{contractDraft.offreSalariale || '-'}</strong></div>
                        </div>

                        <div className="apeFormGrid">
                            <div className="apeField">
                                <label>Type de contrat</label>
                                <select
                                    value={contractDraft.typeContrat}
                                    onChange={(e) => updateContractField('typeContrat', e.target.value)}
                                >
                                    {CONTRACT_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="apeField">
                                <label>Date de début</label>
                                <input
                                    type="date"
                                    value={contractDraft.dateDebut || ''}
                                    onChange={(e) => updateContractField('dateDebut', e.target.value)}
                                />
                            </div>
                            <div className="apeField">
                                <label>Durée du contrat si CDD</label>
                                <input
                                    type="date"
                                    value={contractDraft.dateFin || ''}
                                    onChange={(e) => updateContractField('dateFin', e.target.value)}
                                />
                            </div>
                            <div className="apeField">
                                <label>Période d’essai</label>
                                <input
                                    type="text"
                                    value={contractDraft.periodeEssai || ''}
                                    onChange={(e) => updateContractField('periodeEssai', e.target.value)}
                                />
                            </div>
                            <div className="apeField">
                                <label>Salaire</label>
                                <input
                                    type="text"
                                    value={contractDraft.salaire || ''}
                                    onChange={(e) => updateContractField('salaire', e.target.value)}
                                />
                            </div>
                            <div className="apeField">
                                <label>Lieu de travail</label>
                                <input
                                    type="text"
                                    value={contractDraft.lieuTravail || ''}
                                    onChange={(e) => updateContractField('lieuTravail', e.target.value)}
                                />
                            </div>
                            <div className="apeField apeField--full">
                                <label>Horaires / régime de travail</label>
                                <input
                                    type="text"
                                    value={contractDraft.regimeTravail || ''}
                                    onChange={(e) => updateContractField('regimeTravail', e.target.value)}
                                />
                            </div>
                            <div className="apeField apeField--full">
                                <label>Commentaire RH</label>
                                <textarea
                                    rows={4}
                                    value={contractDraft.commentaireRh || ''}
                                    onChange={(e) => updateContractField('commentaireRh', e.target.value)}
                                />
                            </div>
                            <div className="apeField apeField--full">
                                <label>Contenu du contrat</label>
                                <textarea
                                    rows={10}
                                    value={contractDraft.contenu || ''}
                                    onChange={(e) => updateContractField('contenu', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showProcedureModal}
                title="Procédure d’intégration"
                onClose={() => setShowProcedureModal(false)}
                size="sm"
                footer={
                    <>
                        <button
                            className="apeBtn apeBtn--primary"
                            type="button"
                            onClick={handleSaveProcedure}
                            disabled={!procedureDraft || isAnyDocumentLoading('procedureIntegration', selectedCandidature?.id)}
                        >
                            <Save size={16} />
                            Enregistrer
                        </button>
                    </>
                }
            >
                {procedureDraft && (
                    <div className="apeIntegrationDocs">
                        <div className="apeIntegrationDoc">
                            <label className="apeIntegrationCheck">
                                <input
                                    type="checkbox"
                                    checked={!!procedureDraft.documentsIntegration?.pieceIdentite?.checked}
                                    onChange={(e) => updateIntegrationDocument('pieceIdentite', 'checked', e.target.checked)}
                                />
                                <span>Pièce d'identité</span>
                            </label>

                            {procedureDraft.documentsIntegration?.pieceIdentite?.checked && (
                                <div className="apeIntegrationControls">
                                    <select
                                        className="apeIntegrationSelect"
                                        value={procedureDraft.documentsIntegration?.pieceIdentite?.type || ''}
                                        onChange={(e) => updateIntegrationDocument('pieceIdentite', 'type', e.target.value)}
                                    >
                                        <option value=""></option>
                                        {PIECE_IDENTITE_TYPES.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    <label className="apeFileButton">
                                        Choisir un fichier
                                        <input
                                            type="file"
                                            onChange={(e) => updateIntegrationDocument('pieceIdentite', 'file', e.target.files?.[0] || null)}
                                        />
                                    </label>
                                    <span className="apeFileName">{getIntegrationDocumentFileName('pieceIdentite')}</span>
                                </div>
                            )}
                        </div>

                        <div className="apeIntegrationDoc">
                            <label className="apeIntegrationCheck">
                                <input
                                    type="checkbox"
                                    checked={!!procedureDraft.documentsIntegration?.bulletinPaie?.checked}
                                    onChange={(e) => updateIntegrationDocument('bulletinPaie', 'checked', e.target.checked)}
                                />
                                <span>Bulletin de paie</span>
                            </label>

                            {procedureDraft.documentsIntegration?.bulletinPaie?.checked && (
                                <div className="apeIntegrationControls">
                                    <label className="apeFileButton">
                                        Choisir un fichier
                                        <input
                                            type="file"
                                            onChange={(e) => updateIntegrationDocument('bulletinPaie', 'file', e.target.files?.[0] || null)}
                                        />
                                    </label>
                                    <span className="apeFileName">{getIntegrationDocumentFileName('bulletinPaie')}</span>
                                </div>
                            )}
                        </div>

                        <div className="apeIntegrationDoc">
                            <label className="apeIntegrationCheck">
                                <input
                                    type="checkbox"
                                    checked={!!procedureDraft.documentsIntegration?.autre?.checked}
                                    onChange={(e) => updateIntegrationDocument('autre', 'checked', e.target.checked)}
                                />
                                <span>Autre</span>
                            </label>

                            {procedureDraft.documentsIntegration?.autre?.checked && (
                                <div className="apeIntegrationControls">
                                    <label className="apeFileButton">
                                        Choisir un fichier
                                        <input
                                            type="file"
                                            onChange={(e) => updateIntegrationDocument('autre', 'file', e.target.files?.[0] || null)}
                                        />
                                    </label>
                                    <span className="apeFileName">{getIntegrationDocumentFileName('autre')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={showRejetModal}
                title="Rejeter la candidature"
                onClose={() => setShowRejetModal(false)}
                footer={
                    <>
                        <button className="apeBtn apeBtn--ghost" type="button" onClick={() => setShowRejetModal(false)}>
                            Annuler
                        </button>
                        <button className="apeBtn apeBtn--danger" type="button" onClick={rejeterCandidature}>
                            Rejeter
                        </button>
                    </>
                }
            >
                <div className="apeForm">
                    <p>
                        Voulez-vous vraiment rejeter la candidature de{' '}
                        <strong>{selectedCandidature ? getCandidatNom(selectedCandidature) : ''}</strong> ?
                    </p>
                    <div className="apeField">
                        <label>Motif de rejet *</label>
                        <textarea
                            rows={4}
                            value={motif}
                            onChange={(e) => setMotif(e.target.value)}
                            placeholder="Précisez le motif du rejet..."
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                open={showAnnulerModal}
                title="Annuler la candidature"
                onClose={() => setShowAnnulerModal(false)}
                footer={
                    <>
                        <button className="apeBtn apeBtn--ghost" type="button" onClick={() => setShowAnnulerModal(false)}>
                            Annuler
                        </button>
                        <button className="apeBtn apeBtn--danger" type="button" onClick={annulerCandidature}>
                            Confirmer l'annulation
                        </button>
                    </>
                }
            >
                <div className="apeForm">
                    <p>
                        Voulez-vous vraiment annuler la candidature de{' '}
                        <strong>{selectedCandidature ? getCandidatNom(selectedCandidature) : ''}</strong> ?
                    </p>
                    <div className="apeField">
                        <label>Motif d'annulation *</label>
                        <textarea
                            rows={4}
                            value={motif}
                            onChange={(e) => setMotif(e.target.value)}
                            placeholder="Précisez le motif de l'annulation..."
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
